import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import Main from '../../Main/components'
import { MAIN_ID } from "../../util/constants";
import { withOSUTheming, Mui, withTableOfContents, useFooterWithTOC, withDarkMode } from '@osu/react-ui'
import { AppContext, AppProvider } from "../context";
import withAuthentication from "../../Authentication/withAuthentication";
import useCssVariableUpdater from "../hooks/useCssVariableUpdater";
import useOnPageLoad from "../hooks/useOnPageLoad";
import Footer from "@osu/footer"
import { Navbar as OSUNavBar } from "@osu/navbar";
import { useLocation } from "react-router-dom"

const fallbackSkipTo = "#" + MAIN_ID

function App(props = {}) {
  const { getConfig, navHeight, darkMode, configStatus } = props
  const { skipToHeadingRef } = useContext(AppContext)
  const { hash } = useLocation()
  const [skipToLink, setSkipToLink] = useState(fallbackSkipTo)
  const footerRef = useRef()
  const appRef = useRef()
  useFooterWithTOC({ ref: footerRef })
  const trigger = Mui.useScrollTrigger({
    disableHysteresis: true,
    threshold: navHeight
  });
  const { isPageLoaded } = useOnPageLoad();
  // when the page is loaded (wait for silent challenge token, if necessary), get the config
  useEffect(() => {
    if(isPageLoaded === true && configStatus === "") getConfig();
  }, [getConfig, isPageLoaded, configStatus]);

  
  useLayoutEffect(() => {
    const currentHeadingID = skipToHeadingRef?.current?.id
    if(isPageLoaded && hash && hash === `#${currentHeadingID}`) {
      setSkipToLink(hash)
    }
  }, [isPageLoaded, hash, skipToHeadingRef])

  return (
    <div className="App" ref={appRef}>
       <OSUNavBar darkMode={darkMode} mainHref={skipToLink || fallbackSkipTo} />
      <Main spacingTop={!trigger} className="anchor-link" id={MAIN_ID} />
      <Footer white={!darkMode} darkMode={darkMode} ref={footerRef} />
    </div>
  );
}

const TOCWrappedApp = withTableOfContents(App)

const errorColors = {
  default: {
    main: '#B80000',
    light: '#DC1C1C',
    dark: '#990000',
    contrastText: '#FFFFFF'
  },
  darkMode: { 
    main: '#FF8080',
    light: '#FFA4A4',
    dark: '#FF6666',
    contrastText: '#FFFFFF'
  }
}

function Provider(props = {}) {
  const { authorized, handleSignIn, authStatus, handleSignOut, darkMode, getConfig, configStatus } = props;
  const { navHeight } = useCssVariableUpdater()
  const skipToHeadingRef = useRef()
  const appContext = {
    authStatus,
    authorized,
    handleSignIn,
    handleSignOut,
    navHeight,
    darkMode,
    skipToHeadingRef
  }
  const appProps = { darkMode, getConfig, navHeight, configStatus };
  let theme = Mui.useTheme()
  Object.assign(theme.palette.error, darkMode ? errorColors.darkMode : errorColors.default)
  
  return (
    <AppProvider value={appContext}>
      <Mui.ThemeProvider theme={theme}>
        <TOCWrappedApp {...appProps} />
      </Mui.ThemeProvider>
    </AppProvider>
  );
}


export default withDarkMode(withOSUTheming(withAuthentication(Provider)))